import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  BadgePercent,
  Handshake,
  Link2,
  Mail,
  Megaphone,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';

const PAGE_BG = '#f4f7fc';

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
};

const audience = [
  {
    title: 'تولیدکنندگان محتوا',
    desc: 'اینفلوئنسر، بلاگر و خالقانی که مخاطبشان به مسیر رشد فردی، روابط و آموزش‌های کاربردی علاقه دارد.',
    icon: Megaphone,
  },
  {
    title: 'مربیان و مشاوران',
    desc: 'کسانی که در حوزهٔ کوچینگ، روان‌شناسی یا توسعهٔ فردی فعال‌اند و می‌خواهند منبع معتبر معرفی کنند.',
    icon: Users,
  },
  {
    title: 'هم‌افزایان آموزشی',
    desc: 'مجموعه‌ها و رسانه‌هایی که کیفیت آموزش «من حقیقی» را می‌شناسند و تمایل به معرفی مسئولانه دارند.',
    icon: Handshake,
  },
];

const principles = [
  'معرفی شفاف و صادقانه؛ بدون وعده‌های غیرواقعی یا تضمین نتیجه‌ای که در آموزش استاندارد وجود ندارد.',
  'رعایت حریم خصوصی مخاطب و عدم ارسال پیام انبوه یا اسپم.',
  'استفاده از متریال رسمی (لوگو، متن و تصاویر) پس از هماهنگی با تیم مجموعه.',
  'احترام به برند «من حقیقی» و عدم تحریف نام، شعار یا محتوای دوره‌ها.',
];

const steps = [
  { n: 1, title: 'ثبت درخواست', desc: 'از طریق تماس با ما، نوع همکاری و مخاطب هدف خود را به‌طور خلاصه اعلام کنید.' },
  { n: 2, title: 'بررسی و تطبیق', desc: 'تیم ما درخواست را با سیاست‌های مجموعه و کیفیت محتوا هم‌راستا می‌کند.' },
  { n: 3, title: 'دریافت لینک اختصاصی', desc: 'در صورت توافق، لینک یا کد همکاری متناسب با کمپین در اختیار شما قرار می‌گیرد.' },
  { n: 4, title: 'پیگیری و گزارش', desc: 'برای شفافیت مالی و پشتیبانی مخاطبان، طبق قرارداد همکاری اقدام می‌شود.' },
];

const Partnership: React.FC = () => {
  return (
    <div className="min-h-screen pb-16 pt-4 text-[#0f172a]" style={{ backgroundColor: PAGE_BG }} dir="rtl">
      <div className="mx-auto w-full max-w-[min(900px,96vw)] px-3 sm:px-5 lg:px-6">
        <nav
          aria-label="مسیر دسترسی"
          className="mb-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-slate-500 sm:text-xs"
        >
          <Link to="/" className="font-medium transition hover:text-[#1C3B9B]">
            خانه
          </Link>
          <span className="text-slate-400 opacity-80">›</span>
          <span className="font-semibold text-slate-700">لینک همکاری</span>
        </nav>

        <motion.header
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
          className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-5 shadow-[0_18px_40px_-28px_rgba(28,59,155,0.35)] sm:p-7"
        >
          <div className="pointer-events-none absolute -left-20 top-0 h-48 w-48 rounded-full bg-gradient-to-br from-[#7c3aed]/12 to-transparent blur-2xl" />
          <div className="pointer-events-none absolute -right-16 bottom-0 h-40 w-40 rounded-full bg-gradient-to-tl from-[#C11277]/10 to-transparent blur-2xl" />
          <motion.div variants={fadeUp} className="relative inline-flex items-center gap-2 rounded-full bg-[#eef2ff] px-3 py-1">
            <Sparkles className="h-3.5 w-3.5 text-[#1C3B9B]" />
            <span className="text-[11px] font-bold text-[#1C3B9B] sm:text-xs">من حقیقی · همکاری معرفی و آموزش</span>
          </motion.div>
          <motion.h1
            variants={fadeUp}
            className="relative mt-3 text-2xl font-extrabold leading-tight text-[#0f172a] sm:text-3xl"
          >
            لینک <span className="bg-gradient-to-l from-[#1C3B9B] to-[#7c3aed] bg-clip-text text-transparent">همکاری</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="relative mt-3 max-w-2xl text-sm leading-7 text-slate-600 sm:text-[15px]">
            مجموعهٔ <strong className="text-slate-800">من حقیقی</strong> سال‌هاست روی مسیر رشد فردی، خودشناسی، روابط و
            مهارت‌های کاربردی زندگی کار می‌کند. اگر دوست دارید دوره‌ها و محصولات آموزشی ما را به مخاطبان درست خودتان
            معرفی کنید، برنامهٔ <strong className="text-slate-800">لینک همکاری</strong> فضایی شفاف و حرفه‌ای برای این
            همراهی است — با احترام به کیفیت آموزش و تجربهٔ یادگیرنده.
          </motion.p>
          <motion.div variants={fadeUp} className="relative mt-5 flex flex-wrap gap-2">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-l from-[#1C3B9B] to-[#7c3aed] px-4 py-2.5 text-sm font-bold text-white shadow-[0_12px_24px_-14px_rgba(28,59,155,0.55)] transition hover:opacity-95"
            >
              درخواست همکاری
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:border-[#1C3B9B]/30 hover:text-[#1C3B9B]"
            >
              مشاهده دوره‌ها
              <Link2 className="h-4 w-4 opacity-70" />
            </Link>
          </motion.div>
        </motion.header>

        <section className="mt-8 grid gap-4 sm:grid-cols-3">
          {[
            { icon: Link2, title: 'لینک اختصاصی', text: 'در صورت تأیید، لینک یا کد معرفی متناسب با کمپین در اختیار شما قرار می‌گیرد.' },
            { icon: BadgePercent, title: 'شرایط شفاف', text: 'جزئیات مالی و بازهٔ کمپین پس از توافق کتبی یا قرارداد همکاری اعلام می‌شود.' },
            { icon: ShieldCheck, title: 'حفظ اعتبار برند', text: 'معرفی مسئولانه هم به مخاطب شما احترام می‌گذارد هم به اعتماد ساخته‌شده روی نام من حقیقی.' },
          ].map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.title}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_10px_28px_-22px_rgba(15,23,42,0.2)] sm:p-5"
              >
                <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#eef2ff] text-[#1C3B9B]">
                  <Icon className="h-5 w-5" strokeWidth={2.2} />
                </div>
                <h2 className="text-sm font-extrabold text-[#0f172a]">{card.title}</h2>
                <p className="mt-2 text-xs leading-6 text-slate-600 sm:text-[13px]">{card.text}</p>
              </div>
            );
          })}
        </section>

        <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-5 sm:p-7">
          <h2 className="text-lg font-extrabold text-[#0f172a] sm:text-xl">این برنامه برای چه کسانی است؟</h2>
          <p className="mt-2 text-sm leading-7 text-slate-600">
            اگر مخاطبان شما به مسیر یادگیری عمیق، کارگاه‌های معناگرا و آموزش‌های ساختارمند علاقه دارند، احتمالاً
            هم‌راستایی خوبی با فضای «من حقیقی» خواهید داشت.
          </p>
          <ul className="mt-5 grid gap-3 sm:grid-cols-3">
            {audience.map((item) => {
              const Icon = item.icon;
              return (
                <li
                  key={item.title}
                  className="rounded-xl border border-slate-100 bg-gradient-to-b from-slate-50/80 to-white p-4"
                >
                  <Icon className="mb-2 h-5 w-5 text-[#7c3aed]" strokeWidth={2} />
                  <p className="text-sm font-bold text-slate-900">{item.title}</p>
                  <p className="mt-1.5 text-xs leading-6 text-slate-600">{item.desc}</p>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-5 sm:p-7">
          <h2 className="text-lg font-extrabold text-[#0f172a] sm:text-xl">مسیر پیشنهادی همکاری</h2>
          <ol className="mt-5 space-y-4">
            {steps.map((s) => (
              <li key={s.n} className="flex gap-3 sm:gap-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#1C3B9B] to-[#7c3aed] text-sm font-black text-white">
                  {s.n}
                </span>
                <div>
                  <p className="text-sm font-extrabold text-slate-900">{s.title}</p>
                  <p className="mt-1 text-xs leading-6 text-slate-600 sm:text-[13px]">{s.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-10 rounded-2xl border border-[#1C3B9B]/15 bg-gradient-to-br from-[#f8fafc] via-white to-[#fdf2f8] p-5 sm:p-7">
          <div className="flex flex-wrap items-start gap-2">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#1C3B9B]" />
            <div>
              <h2 className="text-lg font-extrabold text-[#0f172a] sm:text-xl">اصول معرفی که با هم رعایت می‌کنیم</h2>
              <ul className="mt-3 list-disc space-y-2 pr-5 text-sm leading-7 text-slate-700 marker:text-[#7c3aed]">
                {principles.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-5 text-center sm:p-7">
          <Mail className="mx-auto h-8 w-8 text-[#1C3B9B]" strokeWidth={2} />
          <h2 className="mt-3 text-lg font-extrabold text-[#0f172a] sm:text-xl">شروع گفتگو</h2>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-7 text-slate-600">
            برای ارسال درخواست لینک همکاری، معرفی رسانه یا پیشنهاد کمپین مشترک، از صفحهٔ تماس با ما استفاده کنید؛
            موضوع پیام را «لینک همکاری» ذکر کنید تا سریع‌تر به دست تیم مربوط برسد.
          </p>
          <Link
            to="/contact"
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gradient-to-l from-[#1C3B9B] to-[#C11277] px-5 py-2.5 text-sm font-bold text-white transition hover:opacity-95"
          >
            رفتن به تماس با ما
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </section>
      </div>
    </div>
  );
};

export default Partnership;
